import React from 'react';
import { MaterialIcon } from '../../ui';
import { activityService, getSessionEvents } from '../../../services/activityService';
import {
  formatDuration, formatExact, formatRelative, formatRole, formatSection, formatShort, plural,
} from './activityFormat';

const iconFor = (eventType = '') => {
  if (eventType === 'login' || eventType === 'logout') return 'login';
  if (eventType === 'section_view') return 'dashboard';
  if (eventType.startsWith('document')) return 'description';
  if (eventType.startsWith('rag_')) return 'auto_awesome';
  if (eventType.startsWith('message')) return 'chat_bubble';
  if (eventType.startsWith('acc')) return 'task_alt';
  if (eventType.startsWith('event')) return 'event';
  if (eventType.startsWith('user_') || eventType.startsWith('profile_')) return 'person';
  if (eventType.startsWith('impersonation')) return 'visibility';
  return 'history';
};

const Chip = ({ icon, children, tone = 'neutral' }) => {
  const tones = {
    neutral: 'bg-section-bg text-slate-600',
    accent: 'bg-secondary/10 text-secondary',
    warn: 'bg-amber-100 text-amber-800',
    dark: 'bg-primary-dark text-white',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${tones[tone]}`}>
      {icon && <MaterialIcon name={icon} size={12} />}
      {children}
    </span>
  );
};

const EventLine = ({ event, name }) => {
  const [open, setOpen] = React.useState(false);
  const label = activityService.formatAction({ action: event.event_type, actor_name: name, details: event.metadata || {} });
  const entries = Object.entries(event.metadata || {}).filter(([k]) => !k.startsWith('_'));
  return (
    <li className="bg-white rounded-2xl border border-amber-100/50">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full text-left flex items-start gap-3 p-3 rounded-2xl hover:bg-main-bg/60 transition-colors"
      >
        <span className="w-8 h-8 rounded-xl bg-section-bg flex items-center justify-center shrink-0">
          <MaterialIcon name={iconFor(event.event_type)} size={16} className="text-secondary" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
            <span className="text-sm font-bold text-primary-dark break-words">{label}</span>
            <span className="text-[11px] text-slate-500 whitespace-nowrap">{formatShort(event.occurred_at)}</span>
          </span>
          {event.event_type === 'documents_fetch_error' && (
            <span className="mt-1 inline-block"><Chip icon="warning" tone="warn">Library failed to load</Chip></span>
          )}
        </span>
      </button>
      {open && (
        <dl className="px-4 pb-3 grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs">
          <dt className="text-slate-500 font-bold">Event</dt>
          <dd className="sm:col-span-2 font-mono text-slate-700 break-all">{event.event_type}</dd>
          {entries.length === 0 && <dd className="sm:col-span-3 text-slate-500 italic">No details recorded.</dd>}
          {entries.map(([k, v]) => (
            <React.Fragment key={k}>
              <dt className="text-slate-500 font-bold capitalize">{k.replace(/_/g, ' ')}</dt>
              <dd className="sm:col-span-2 text-slate-700 break-words">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}
    </li>
  );
};

/**
 * One server-side session. Events load lazily on first expand; background
 * (diagnostic) events are collapsed behind "+N background events".
 */
const SessionRow = ({ session, hoaId }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [events, setEvents] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [showBackground, setShowBackground] = React.useState(false);

  const toggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (!next || events || loading) return;
    setLoading(true);
    setError('');
    try {
      const rows = await getSessionEvents({
        hoaId,
        userId: session.user_id,
        from: session.started_at,
        to: session.ended_at,
        viewAs: session.admin_view_as,
      });
      setEvents(rows);
    } catch (err) {
      setError(err?.message || 'Events could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  const name = session.full_name;
  const sections = (session.sections || []).map(formatSection).filter(Boolean);
  const visible = (events || []).filter((e) => !e.isBackground).reverse();
  const background = (events || []).filter((e) => e.isBackground).reverse();
  const hasFetchError = background.some((e) => e.event_type === 'documents_fetch_error');
  const headline = session.admin_view_as
    ? `${name} viewed by admin`
    : session.login_anchored ? `${name} signed in` : `${name} was active`;

  return (
    <li className="bg-white rounded-2xl border border-amber-100/30 warm-tactile-shadow">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={expanded}
        className="w-full text-left p-4 sm:p-5 flex items-start gap-3 sm:gap-4"
      >
        <span className="w-10 h-10 rounded-xl bg-section-bg flex items-center justify-center shrink-0">
          <MaterialIcon
            name={session.admin_view_as ? 'visibility' : session.login_anchored ? 'login' : 'history'}
            size={20}
            className="text-secondary"
          />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-sm sm:text-base font-bold text-primary-dark break-words">{headline}</span>
            {session.role_name && <Chip>{formatRole(session.role_name)}</Chip>}
            {session.account_state === 'archived' && <Chip icon="inventory_2">Archived</Chip>}
            {session.admin_view_as && <Chip icon="visibility" tone="accent">Admin view-as</Chip>}
          </span>
          <span className="block text-xs text-slate-500 mt-1">
            <span title={formatExact(session.started_at)}>{formatShort(session.started_at)}</span>
            {' · '}{formatDuration(session.started_at, session.ended_at)}
            {' · '}{plural(session.event_count, 'event')}
            {' · '}{formatRelative(session.ended_at)}
          </span>
          <span className="flex flex-wrap items-center gap-2 mt-2">
            {session.doc_views > 0 && (
              <Chip icon="description" tone="accent">
                {plural(session.doc_views, 'document')} opened
              </Chip>
            )}
            {sections.slice(0, 6).map((s) => <Chip key={s}>{s}</Chip>)}
            {session.background_count > 0 && (
              <span className="text-[11px] text-slate-500">+{session.background_count} background</span>
            )}
          </span>
        </span>
        <MaterialIcon name={expanded ? 'expand_less' : 'expand_more'} size={22} className="text-secondary shrink-0" />
      </button>

      {expanded && (
        <div className="px-4 sm:px-5 pb-5">
          <div className="bg-section-bg rounded-2xl p-3 sm:p-4">
            {loading && <p className="text-sm text-slate-500 py-4 text-center">Loading events…</p>}
            {error && <p className="text-sm text-red-700 py-4 text-center">{error}</p>}
            {events && !loading && (
              <>
                {visible.length === 0 && (
                  <p className="text-sm text-slate-500 py-4 text-center">No events in this session.</p>
                )}
                <ul className="space-y-2">
                  {visible.map((e) => <EventLine key={e.id} event={e} name={name} />)}
                </ul>
                {background.length > 0 && (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => setShowBackground((v) => !v)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-amber-100/60 bg-white text-xs font-bold text-slate-600 hover:border-secondary/40 transition-colors"
                    >
                      <MaterialIcon name={showBackground ? 'expand_less' : 'expand_more'} size={14} />
                      {showBackground ? 'Hide' : `+${background.length}`} background {background.length === 1 ? 'event' : 'events'}
                      {hasFetchError && <MaterialIcon name="warning" size={14} className="text-amber-700" />}
                    </button>
                    {showBackground && (
                      <ul className="space-y-2 mt-2 opacity-80">
                        {background.map((e) => <EventLine key={e.id} event={e} name={name} />)}
                      </ul>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </li>
  );
};

export default SessionRow;
